import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SecurityBadges from '../components/SecurityBadges';
import { getVerificationByStripeSessionId, updateVerificationStatus } from '../services/stripeService';
import { getLeadBySessionId } from '../services/leadsService';
import { trackStepCompletion } from '../services/analyticsService';
import { getSessionId } from '../lib/supabase';
import { logger } from '../lib/utils';

const VerificationSuccess: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [leadName, setLeadName] = useState<string | null>(null);
  const [auftragsnummer, setAuftragsnummer] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    // Try to get the verification_session_id from the URL query parameters
    const queryParams = new URLSearchParams(location.search);
    const verificationSessionId = queryParams.get('session_id');
    
    // If there's a session ID, try to process it
    if (verificationSessionId) {
      const processVerification = async () => {
        try {
          setIsLoading(true);
          setError(null);
          
          logger.log('Processing verification with session ID:', verificationSessionId);
          
          // Get the verification record
          const verification = await getVerificationByStripeSessionId(verificationSessionId);
          
          if (verification) {
            // Update the verification status to 'verified'
            await updateVerificationStatus(verification.id, 'verified');
            
            // Get the lead information
            if (verification.lead_id && verification.session_id) {
              const lead = await getLeadBySessionId(verification.session_id);
              
              if (lead) {
                setLeadName(`${lead.first_name} ${lead.last_name}`);
                
                if (lead.auftragsnummer) {
                  setAuftragsnummer(lead.auftragsnummer);
                }

                // Track the verification success step
                const sessionId = verification.session_id || await getSessionId();
                
                // Use 'verification_success' as the step name to match naming convention
                await trackStepCompletion(sessionId, 'verification_success', {
                  verification_id: verification.id,
                  lead_id: verification.lead_id,
                  verification_session_id: verificationSessionId,
                  email: verification.email,
                  status: 'verified',
                  auftragsnummer: lead.auftragsnummer,
                  // Include all lead data to ensure form_data is properly populated
                  first_name: lead.first_name,
                  last_name: lead.last_name,
                  phone: lead.phone,
                  address: lead.address,
                  city: lead.city,
                  postal_code: lead.postal_code,
                  country: lead.country,
                  lead_data: JSON.stringify(lead)
                }, location.search);
                
                logger.log('Tracked verification_success funnel step with complete lead data');
              }
            } else {
              // If we don't have a lead_id or session_id, still track the step with available data
              const sessionId = await getSessionId();
              await trackStepCompletion(sessionId, 'verification_success', {
                verification_id: verification.id,
                verification_session_id: verificationSessionId,
                email: verification.email,
                status: 'verified'
              }, location.search);
              
              logger.log('Tracked verification_success funnel step (limited data)');
            }
          } else {
            // Even if we couldn't get verification details, still track the page view
            const sessionId = await getSessionId();
            await trackStepCompletion(sessionId, 'verification_success', {
              verification_session_id: verificationSessionId,
              status: 'unknown'
            }, location.search);
            
            logger.log('Tracked verification_success page view (no verification data)');
          }
          
          setIsLoading(false);
        } catch (error) {
          logger.error('Error processing verification:', error);
          // Don't set an error, just show the generic success message
          setIsLoading(false);
          
          // Still try to track the step even if there was an error
          try {
            const sessionId = await getSessionId();
            await trackStepCompletion(sessionId, 'verification_success', {
              verification_session_id: verificationSessionId,
              status: 'error',
              error: (error as Error).message
            }, location.search);
            
            logger.log('Tracked verification_success with error');
          } catch (trackingError) {
            logger.error('Error tracking verification_success step:', trackingError);
          }
        }
      };
      
      processVerification();
    } else {
      // If there's no session ID, still track the page view
      const trackPageView = async () => {
        try {
          const sessionId = await getSessionId();
          await trackStepCompletion(sessionId, 'verification_success', {
            status: 'no_session_id'
          }, location.search);
          
          logger.log('Tracked verification_success page view (no session ID)');
        } catch (error) {
          logger.error('Error tracking verification_success page view:', error);
        }
      };
      
      trackPageView();
    }
  }, [location.search]);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-grow container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-md p-6 md:p-8">
          <h1 className="text-2xl md:text-3xl font-bold text-center text-primary mb-6">
            Identitätsverifizierung erfolgreich!
          </h1>
          
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-gray-600">Wir verarbeiten Ihre Verifizierung...</p>
            </div>
          ) : (
            <>
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-6">
                <p className="font-medium">Ihre Identität wurde erfolgreich verifiziert!</p>
                <p className="mt-2">
                  Vielen Dank, dass Sie die gesetzlich vorgeschriebene Identitätsverifizierung abgeschlossen haben. Wir können nun mit der Bearbeitung Ihres Auftrags fortfahren.
                </p>
                {auftragsnummer && (
                  <p className="mt-2">
                    Ihre Auftragsnummer: <span className="font-bold">{auftragsnummer}</span>
                  </p>
                )}
              </div>
              
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 my-6">
                <p className="mb-0">
                  Sollten Sie noch weitere Fragen haben, wenden Sie sich gerne per E-Mail unter <a href="mailto:info@adressfinder.org" className="text-deep-blue font-medium">info@adressfinder.org</a> an uns{auftragsnummer && <span> und geben Sie Ihre Auftragsnummer <span className="font-medium">{auftragsnummer}</span> an</span>}.
                </p>
              </div>
              
              <div className="mt-8">
                <h2 className="text-xl font-semibold mb-4">Was passiert als Nächstes?</h2>
                <ol className="list-decimal list-inside space-y-3 text-gray-700">
                  <li>
                    <span className="font-medium">Bearbeitung Ihres Auftrags:</span> Unser Team beginnt jetzt mit der Bearbeitung Ihres Auftrags.
                  </li>
                  <li>
                    <span className="font-medium">Ergebnisse:</span> Wir senden Ihnen die Ergebnisse per E-Mail zu.
                  </li>
                  <li>
                    <span className="font-medium">Kundensupport:</span> Bei Fragen können Sie sich jederzeit an unseren Kundendienst wenden.
                  </li>
                </ol>
              </div>
              
              <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-blue-800">
                  {leadName ? (
                    <>Vielen Dank, {leadName}, für Ihr Vertrauen in unseren Service.</>
                  ) : (
                    <>Vielen Dank für Ihr Vertrauen in unseren Service.</>
                  )}
                </p>
                <p className="mt-2 text-blue-800">
                  Wir werden Sie über den Fortschritt Ihres Auftrags per E-Mail auf dem Laufenden halten.
                </p>
              </div>
            </>
          )}
        </div>
        
        <div className="mt-8">
          <SecurityBadges />
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default VerificationSuccess; 